// cleanup_convert_to_glu_component_GENERICS
// cleanup_convert_to_typescript_file_GENERICS

import React, { useRef, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { useTrapFocus } from '@glu/utilities-react';
import { Slide } from '@glu/transitions-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import {
  CheckmarkCircleNextIcon,
  XCircleIcon,
  ExclamationCircleNextIcon,
  ExclamationTriangleNextIcon,
  InfoCircleNextIcon
} from '@glu/icons-react';

import useKeyHandler from 'hooks/useKeyHandler';
import useModalFocus from 'hooks/useModalFocus';
import { DEFAULT_PROPS } from 'constants.js';

import useStyles from './fullscreenModal.styles';

const icons = {
  success: CheckmarkCircleNextIcon,
  error: ExclamationTriangleNextIcon,
  warning: ExclamationCircleNextIcon,
  info: InfoCircleNextIcon,
  none: ''
};

export function tab(event) {
  if (!event) {
    /* istanbul ignore next */
    return false;
  }

  if (event.key) {
    /* istanbul ignore next */
    return event.key.toLowerCase() === 'tab';
  }

  return (event.keyIdentifier || event.keyCode) === 9;
}

const { EMPTY_ARRAY, EMPTY_FUNCTION } = DEFAULT_PROPS;

const FullscreenModal = ({
  children = EMPTY_ARRAY,
  className = '',
  dataQa = null,
  header = null,
  icon = '',
  mountOnEnter = true,
  onHide = EMPTY_FUNCTION,
  show = false,
  showIcon = true,
  transition: Transition = Slide,
  unmountOnExit = true,
  variant = null,
  verticalPosition = 0,
  withParams = false
}) => {
  const classes = useStyles();
  const [transitionIn, setTransitionIn] = useState(true);
  const Icon = icon ? icons[icon] : icons.error;
  const [initial, setInitial] = useState(true);
  const modalRootRef = useRef();

  useEffect(() => {
    document.body.classList[show ? 'add' : 'remove']('modal--opened');
    return () => {
      document.body.classList.remove('modal--opened');
    };
  }, [show]);

  useEffect(() => setInitial(false), []);
  useEffect(() => {
    if (!transitionIn) {
      const timeout = setTimeout(() => {
        setTransitionIn(true);

        if (!initial) {
          onHide();
        }
      }, 0.5);

      return () => {
        clearTimeout(timeout);
        document.body.classList.remove('modal--opened');
      };
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onHide, transitionIn]);

  const closeModal = useCallback(
    (event) => {
      if (tab(event)) {
        /* istanbul ignore next */
        return;
      }

      if (typeof Transition === 'string') {
        /* istanbul ignore next */
        onHide();
        return;
      }

      setTransitionIn(false);
    },
    [Transition, onHide]
  );

  const transitionProps =
    typeof Transition === 'string'
      ? {}
      : {
          in: show && transitionIn,
          mountOnEnter,
          unmountOnExit
        };

  const getAriaLabel = () => 'Dialog';

  // Focus modal on open, focus previous element on close
  useModalFocus({ ref: modalRootRef, active: show });

  // Escape key to close modal
  useKeyHandler({ keyEvent: 'keyup', keyCode: 27, callback: closeModal });

  const onKeyDown = useTrapFocus({ ref: modalRootRef, active: show });

  return (
    <section className={classes.modalComponent}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={modalRootRef}
        aria-label={getAriaLabel()}
        className={classNames(className, classes.root, { [classes.modalIn]: show })}
        data-qa={dataQa}
        onKeyDown={onKeyDown}
        role="dialog"
        style={{ top: `${verticalPosition}px` }}
        tabIndex="-1"
      >
        <Transition {...transitionProps}>
          <article
            className={classNames(classes.dialog, 'dialog')}
            data-qa="full-screen-modal"
            role="document"
          >
            <Button
              className={classes.close}
              data-qa="full-screen-modal-close"
              onClick={closeModal}
              variant={TERTIARY}
            >
              <span className="sr-only">Close Transfer Import dialog</span>
              <XCircleIcon aria-hidden focusable="false" title="Close Transfer Import dialog" />
            </Button>
            <div className={classNames(classes.content, classes[variant])}>
              <header className={classes.header}>
                {showIcon && <Icon className={classes.icon} />}
                <br />
                {typeof header === 'string' ? <h1 className={classes.title}>{header}</h1> : header}
              </header>

              <section className={classes.modalBody}>
                {withParams ? children({ modalRootRef: modalRootRef.current }) : children}
              </section>
            </div>
          </article>
        </Transition>
      </div>
    </section>
  );
};

FullscreenModal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  classes: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  dataQa: PropTypes.string,
  header: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  icon: PropTypes.string,
  mountOnEnter: PropTypes.bool,
  onHide: PropTypes.func,
  show: PropTypes.bool,
  showIcon: PropTypes.bool,
  transition: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.string,
    PropTypes.shape({ render: PropTypes.func.isRequired }),
    PropTypes.instanceOf(Slide)
  ]),
  unmountOnExit: PropTypes.bool,
  variant: PropTypes.oneOf(['info', 'success', 'error', 'warning']),
  withParams: PropTypes.bool,
  verticalPosition: PropTypes.number
};

export default FullscreenModal;
